import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Fuel, Gauge, Loader2 } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1580654712603-eb43273aff33?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzcG9ydCUyMGNhciUyMG5pZ2h0fGVufDB8fHx8MTc4NTIyNzI3M3ww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, mfaVerify, formatApiError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [mfaChallenge, setMfaChallenge] = useState(null); // { tmp_token }
  const [mfaCode, setMfaCode] = useState("");
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res && res.mfa_required) {
        setMfaChallenge({ tmp_token: res.tmp_token });
        toast.info("Enter your authenticator code");
        return;
      }
      toast.success("Signed in");
      const nextPath = res && res.is_super_admin ? "/admin" : "/";
      navigate(nextPath);
    } catch (e2) {
      const msg = formatApiError(e2);
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const onMfaSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await mfaVerify(mfaChallenge.tmp_token, mfaCode);
      toast.success("Signed in");
      navigate(u && u.is_super_admin ? "/admin" : "/");
    } catch (e2) {
      const msg = formatApiError(e2);
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0A0A]">
      <div className="hidden lg:block relative overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A]/70 via-[#0A0A0A]/40 to-[#0A0A0A]" />
        <div className="relative h-full flex flex-col justify-between p-10 xl:p-14">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FF5A1F] flex items-center justify-center">
              <Fuel size={22} strokeWidth={2.5} className="text-black" />
            </div>
            <span className="font-display text-2xl font-black tracking-tighter">RAYFUEL</span>
          </div>
          <div className="max-w-lg">
            <p className="ft-overline mb-4 text-[#FF5A1F]">TELEMETRY-GRADE FUEL LOGGING</p>
            <h1 className="font-display text-5xl xl:text-6xl font-black tracking-tighter leading-none">
              Every drop.<br />Every kilometre.<br />Measured.
            </h1>
            <p className="mt-6 text-[#A3A3A3] text-base leading-relaxed max-w-md">
              Snap a receipt. Log the odometer. RayFuel does the math — mileage, cost per km,
              worst-run vehicle, weekly spend — so your fleet stops burning cash silently.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
              <div className="border border-[#262626] p-4 bg-[#0A0A0A]/60 backdrop-blur">
                <Gauge size={18} className="text-[#FF5A1F]" />
                <div className="ft-metric text-xl mt-2">km/l</div>
                <div className="ft-overline text-[10px] mt-1">Per-fillup mileage</div>
              </div>
              <div className="border border-[#262626] p-4 bg-[#0A0A0A]/60 backdrop-blur">
                <Fuel size={18} className="text-[#FF5A1F]" />
                <div className="ft-metric text-xl mt-2">₹/km</div>
                <div className="ft-overline text-[10px] mt-1">Real-cost tracker</div>
              </div>
            </div>
          </div>
          <div className="ft-overline">01 / OPERATIONAL</div>
        </div>
      </div>

      <div
        className="relative flex items-center justify-center p-6 sm:p-10 min-h-screen lg:min-h-0 bg-cover bg-center bg-no-repeat lg:bg-none"
        style={{
          backgroundImage: "linear-gradient(rgba(10,10,10,0.62), rgba(10,10,10,0.82)), url('/images/rayfuel-login-bg.png')"
        }}
      >
        <form onSubmit={onSubmit} className="w-full max-w-md ft-fade-up" data-testid="login-form">
          <p className="ft-overline mb-3">SIGN IN</p>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none mb-8">
            {mfaChallenge ? <>Enter your<br />authenticator code.</> : <>Welcome<br />back, driver.</>}
          </h2>

          {mfaChallenge ? (
            <>
              <label className="ft-overline block mb-2">6-digit TOTP code</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                required
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F] focus:ring-1 focus:ring-[#FF5A1F] mb-6 text-2xl tracking-widest"
                placeholder="123456"
                autoFocus
                data-testid="login-mfa-input"
              />
              {err && <div className="mb-4 text-[#EF4444] text-sm border border-[#EF4444]/30 bg-[#EF4444]/10 p-3">{err}</div>}
              <button
                type="button"
                onClick={onMfaSubmit}
                disabled={loading || mfaCode.length !== 6}
                className="w-full bg-[#FF5A1F] text-black font-bold px-6 py-3 hover:bg-[#E04F1A] disabled:opacity-60 inline-flex items-center justify-center gap-2"
                data-testid="login-mfa-submit"
              >
                {loading && <Loader2 size={16} className="animate-spin" />} Verify
              </button>
              <button type="button" onClick={() => { setMfaChallenge(null); setMfaCode(""); setErr(""); }} className="mt-3 text-xs text-[#A3A3A3] hover:text-white">
                Back to email/password
              </button>
            </>
          ) : (
            <>
              <label className="ft-overline block mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F] focus:ring-1 focus:ring-[#FF5A1F] transition-colors duration-200 mb-5"
                placeholder="you@example.com"
                data-testid="login-email-input"
              />

              <label className="ft-overline block mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F] focus:ring-1 focus:ring-[#FF5A1F] transition-colors duration-200 mb-6"
                placeholder="••••••••"
                data-testid="login-password-input"
              />

              {err && (
                <div className="mb-4 text-[#EF4444] text-sm border border-[#EF4444]/30 bg-[#EF4444]/10 p-3">
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF5A1F] text-black font-bold px-6 py-3 hover:bg-[#E04F1A] transition-colors duration-200 disabled:opacity-60 inline-flex items-center justify-center gap-2"
                data-testid="login-submit-button"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                Sign in
              </button>
            </>
          )}

          <p className="mt-6 text-[#A3A3A3] text-sm">
            No account?{" "}
            <Link to="/signup" className="text-[#FF5A1F] hover:underline" data-testid="link-signup">
              Create one
            </Link>
            <span className="mx-2 text-[#404040]">·</span>
            <Link to="/forgot" className="text-[#FF5A1F] hover:underline" data-testid="link-forgot">
              Forgot password?
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
